import React from 'react';
import { Column } from 'react-table';
import SessionTable, {
  ResponseGuard,
  SessionTableInstanceProps
} from '../index';
import { MappingCell, YesNoCell, NAGuard, CellFunction } from '../../cells';
import Tooltip from '../../../tooltip';
import Icon from '../../../../atoms/icon';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { IEpxCOPD, Variant } from '../../../../../libs/apps/epxcopd';
import { TriStateLiteral } from '../../../../../libs/session';
import { useTerms } from '../../../../../contexts/terms';

type Dependencies = [Variant, boolean, boolean];

interface SputumHeaderProps {
  title: string;
}

const getDependencies = (sessions: IEpxCOPD[]): Dependencies => {
  let variant: Variant = null;
  let shouldAskNebulizer = false;
  let shouldAskFever = false;
  for (const session of sessions) {
    if (session.variant === Variant.B) {
      variant = Variant.B;
    }
    if (session.shouldAskNebulizer) {
      shouldAskNebulizer = true;
    }
    if (session.shouldAskFever) {
      shouldAskFever = true;
    }
  }
  return [variant, shouldAskNebulizer, shouldAskFever];
};

const SputumHeader = ({ title }: SputumHeaderProps) => {
  const { terms } = useTerms();
  return (
    <Tooltip
      content={`Some values for this column will be "N/A" if the ${terms.patient.default} replied "no cough" to the coughing question.`}
    >
      <span>
        {title}
        <Icon className="ml-2" icon={faInfoCircle} />
      </span>
    </Tooltip>
  );
};

const GetNACheckSputum = () => {
  return (session: IEpxCOPD): boolean => {
    return session.coughing === 2;
  };
};

const getColumns = (sessions: IEpxCOPD[]): Column<IEpxCOPD>[] => {
  const [variant, shouldAskNebulizer, shouldAskFever] =
    getDependencies(sessions);
  const columns: Column<IEpxCOPD>[] = [
    {
      Header: 'Breathing',
      id: 'breathing',
      Cell: ResponseGuard(
        MappingCell('breathing', TriStateLiteral) as CellFunction<IEpxCOPD>
      )
    },
    {
      Header: 'Coughing',
      id: 'coughing',
      Cell: ResponseGuard(
        MappingCell('coughing', {
          [-1]: 'Worse',
          0: 'Same',
          1: 'Better',
          2: 'No cough'
        }) as CellFunction<IEpxCOPD>
      )
    },
    {
      Header: <SputumHeader title="Sputum" />,
      id: 'sputum',
      Cell: NAGuard(
        ResponseGuard(
          MappingCell('sputum', {
            1: 'Same',
            2: 'Larger Amount',
            3: 'Different Color'
          }) as CellFunction<IEpxCOPD>
        ),
        GetNACheckSputum()
      )
    }
  ];
  if (['B'].includes(variant)) {
    columns.push({
      Header: 'Wants Outreach',
      accessor: 'wantsToSpeak',
      Cell: ResponseGuard(YesNoCell('wantsToSpeak') as CellFunction<IEpxCOPD>)
    });
  }
  if (shouldAskNebulizer) {
    columns.push({
      Header: 'Used Nebulizer',
      accessor: 'hasUsedNebulizer',
      Cell: ResponseGuard(
        YesNoCell('hasUsedNebulizer') as CellFunction<IEpxCOPD>
      )
    });
  }
  if (shouldAskFever) {
    columns.push({
      Header: 'Has Fever',
      accessor: 'hasFever',
      Cell: ResponseGuard(YesNoCell('hasFever') as CellFunction<IEpxCOPD>)
    });
  }
  return columns;
};

const EpxCOPDTable: React.FC<SessionTableInstanceProps> = (
  props
): JSX.Element => {
  return (
    <SessionTable<IEpxCOPD>
      url="/epxcopd/data/"
      getColumns={getColumns}
      getDependencies={getDependencies}
      {...props}
    />
  );
};

export default EpxCOPDTable;
